import React, { useState, useRef, useEffect } from 'react';
import styles from './VideoTimeline.module.css';
import { MediaItem } from '../../types';

type ResizeType = 'left' | 'right';

interface ResizingState {
  markerId: string;
  type: ResizeType;
  initialX: number;
  initialStart: number;
  initialEnd: number;
}

interface MediaMarkersProps {
  mediaItems: MediaItem[];
  duration: number;
  segmentColors: string[];
  onMediaMarkerClick: (e: React.MouseEvent, mediaItem: MediaItem) => void;
  onMediaMarkersUpdate?: (updatedMedia: MediaItem[]) => void;
  setMediaItems: React.Dispatch<React.SetStateAction<MediaItem[]>>;
  selectedMediaItemId?: string | null; // NEW: selected media item
  setSelectedMediaItemId?: React.Dispatch<React.SetStateAction<string | null>>; // NEW: setter for selected media item
}

const MediaMarkers: React.FC<MediaMarkersProps> = ({
  mediaItems,
  duration,
  segmentColors,
  onMediaMarkerClick,
  onMediaMarkersUpdate,
  setMediaItems,
  selectedMediaItemId, // NEW: selected media item
  setSelectedMediaItemId, // NEW: setter for selected media item
}) => {
  // For dragging entire marker:
  const [draggedItem, setDraggedItem] = useState<MediaItem | null>(null);
  const [dragOffset, setDragOffset] = useState<number>(0);
  const [previewPositions, setPreviewPositions] = useState<MediaItem[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const containerRectRef = useRef<DOMRect | null>(null);

  // For resizing marker edges:
  const [resizingState, setResizingState] = useState<ResizingState | null>(null);

  // Convert a time (in seconds) to a percentage of the timeline.
  const timeToPercent = (time: number) => (time / duration) * 100;
  // Convert pixel displacement to time (using container width).
  const pixelToTime = (pixel: number) => {
    if (!containerRectRef.current) return 0;
    const containerWidth = containerRectRef.current.width;
    return (pixel / containerWidth) * duration;
  };

  // --- DRAGGING (Reposition entire marker) ---
  const handleDragStart = (e: React.MouseEvent, item: MediaItem) => {
    if (resizingState) return; // Don't start drag if resizing is active.
    e.preventDefault();
    e.stopPropagation();
    if (containerRef.current) {
      containerRectRef.current = containerRef.current.getBoundingClientRect();
    }
    const rect = (e.target as HTMLElement).getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    setDraggedItem(item);
    setDragOffset(offsetX);
    setPreviewPositions([...mediaItems]);
    
    // NEW: Select the item being dragged
    if (setSelectedMediaItemId) {
      setSelectedMediaItemId(item.id);
    }
  };

  const handleDrag = (clientX: number) => {
    if (!draggedItem || !containerRectRef.current) return;
    const relativeX = clientX - containerRectRef.current.left - dragOffset;
    const newTime = pixelToTime(relativeX);
    const itemDuration = draggedItem.end - draggedItem.start;
    const newStart = Math.max(0, newTime);
    const newEnd = newStart + itemDuration;
    const updatedPositions = mediaItems.map(item =>
      item.id === draggedItem.id ? { ...item, start: newStart, end: newEnd } : item
    );
    setPreviewPositions(updatedPositions);
  };

  useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (draggedItem) {
        handleDrag(e.clientX);
      }
    };
    if (draggedItem) {
      window.addEventListener('mousemove', handleGlobalMouseMove);
    }
    return () => {
      window.removeEventListener('mousemove', handleGlobalMouseMove);
    };
  }, [draggedItem, dragOffset, mediaItems]);

  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (draggedItem) {
        if (onMediaMarkersUpdate) {
          onMediaMarkersUpdate(previewPositions);
        }
        setDraggedItem(null);
        setPreviewPositions([]);
        containerRectRef.current = null;
      }
    };
    window.addEventListener('mouseup', handleGlobalMouseUp);
    return () => {
      window.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [draggedItem, onMediaMarkersUpdate, previewPositions]);

  // --- RESIZING (Adjust marker's start/end) ---
  const handleResizeStart = (
    e: React.MouseEvent,
    item: MediaItem,
    type: ResizeType
  ) => {
    e.stopPropagation();
    if (containerRef.current) {
      containerRectRef.current = containerRef.current.getBoundingClientRect();
    }
    setResizingState({
      markerId: item.id,
      type,
      initialX: e.clientX,
      initialStart: item.start,
      initialEnd: item.end,
    });
    
    // NEW: Select the item being resized
    if (setSelectedMediaItemId) {
      setSelectedMediaItemId(item.id);
    }
  };

  useEffect(() => {
    const handleGlobalMouseMoveResize = (e: MouseEvent) => {
      if (resizingState && containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        const timePerPixel = duration / containerWidth;
        const deltaX = e.clientX - resizingState.initialX;
        const deltaTime = deltaX * timePerPixel;
        setMediaItems(prev =>
          prev.map(item => {
            if (item.id === resizingState.markerId) {
              if (resizingState.type === 'left') {
                if (item.type === 'video') {
                  // For video, clamp new start so that new duration doesn't exceed original length.
                  const lowerBound = item.end - item.length;
                  const newStartCandidate = resizingState.initialStart + deltaTime;
                  const clampedNewStart = Math.min(item.end - 0.1, Math.max(lowerBound, newStartCandidate));
                  return { ...item, start: clampedNewStart };
                } else {
                  return {
                    ...item,
                    start: Math.min(item.end - 0.1, Math.max(0, resizingState.initialStart + deltaTime)),
                  };
                }
              } else {
                let newEnd = Math.max(item.start + 0.1, resizingState.initialEnd + deltaTime);
                if (item.type === 'video') {
                  const maxEnd = item.start + item.length;
                  newEnd = Math.min(newEnd, maxEnd);
                }
                return { ...item, end: newEnd };
              }
            }
            return item;
          })
        );
      }
    };

    const handleGlobalMouseUpResize = () => {
      if (resizingState) {
        setResizingState(null);
      }
    };

    if (resizingState) {
      window.addEventListener('mousemove', handleGlobalMouseMoveResize);
      window.addEventListener('mouseup', handleGlobalMouseUpResize);
    }
    return () => {
      window.removeEventListener('mousemove', handleGlobalMouseMoveResize);
      window.removeEventListener('mouseup', handleGlobalMouseUpResize);
    };
  }, [resizingState, duration, setMediaItems]);

  // Alignment check remains unchanged.
  const alignmentThreshold = 0.05;
  const currentDragged = draggedItem ? previewPositions.find(item => item.id === draggedItem.id) : null;
  const isAligned =
    currentDragged &&
    previewPositions.some(item =>
      item.id !== currentDragged.id &&
      (Math.abs(currentDragged.end - item.start) < alignmentThreshold ||
        Math.abs(currentDragged.start - item.end) < alignmentThreshold)
    );

  // --- Compute unconnected items for flagging ---
  // Assume mediaItems is sorted by start time.
  const threshold = 0.05;
  const sortedItems = [...mediaItems].sort((a, b) => a.start - b.start);
  const unconnectedMap = new Map<string, { left: boolean; right: boolean }>();
  sortedItems.forEach((item, index) => {
    const leftUnconnected =
      index === 0 ? item.start > threshold : item.start - sortedItems[index - 1].end > threshold;
    const rightUnconnected =
      index === sortedItems.length - 1
        ? duration - item.end > threshold
        : sortedItems[index + 1].start - item.end > threshold;
    unconnectedMap.set(item.id, { left: leftUnconnected, right: rightUnconnected });
  });

  return (
    <div 
      ref={containerRef}
      className={styles.mediaMarkersContainer}
    >
      {/* Track Labels */}
      <div className={styles.trackLabels}>
        <div className={`${styles.trackLabel} ${styles.images}`}>Images</div>
        <div className={`${styles.trackLabel} ${styles.videos}`}>Videos</div>
      </div>

      {/* Render preview markers during dragging */}
      {draggedItem && previewPositions.map((item, index) => {
        const isHiddenPreview = item.hidden || false;
        const previewTrackClass = isHiddenPreview 
          ? styles.hiddenTrack 
          : (item.type === 'video' ? styles.videoTrack : styles.imageTrack);
        
        return (
          <div
            key={`preview-${item.id}-${index}`}
            className={`${styles.mediaMarker} ${styles.mediaMarkerPreview} ${previewTrackClass}`}
            style={{
              left: `${timeToPercent(item.start)}%`,
              width: `${timeToPercent(item.end - item.start)}%`,
              backgroundColor: segmentColors[index % segmentColors.length],
              opacity: draggedItem?.id === item.id && isAligned ? 1 : 0.3,
              border: draggedItem?.id === item.id && isAligned ? '2px solid green' : '1px dashed #666',
            }}
          >
            {item.type === "video" ? (
              <video src={item.url} className={styles.mediaVideo} muted />
            ) : (
              item.src ? (
                <img src={item.src} alt={item.name} className={styles.mediaImage} />
              ) : (
                <div className={styles.mediaText}>{item.name}</div>
              )
            )}
          </div>
        );
      })}

      {/* Render the actual markers with track separation */}
      {mediaItems.map((item, index) => {
        const unconnected = unconnectedMap.get(item.id);
        const isHidden = item.hidden || false; // Check if item is hidden
        
        // Determine track class based on hidden status first, then type
        const trackClass = isHidden 
          ? styles.hiddenTrack 
          : (item.type === 'video' ? styles.videoTrack : styles.imageTrack);
        
        // Check if segment is not loaded yet (for lazy loading)
        const isNotLoaded = item.isLoaded === false;
        
        // Check if item has an active transition to the next item
        const hasActiveTransition = !!item.transitionType && !!item.transitionDuration;
        
        // Check if the PREVIOUS item has a VALID transition to THIS item
        const sortedItems = [...mediaItems].filter(i => !i.hidden).sort((a, b) => a.start - b.start);
        const currentIndex = sortedItems.findIndex(i => i.id === item.id);
        const previousItem = currentIndex > 0 ? sortedItems[currentIndex - 1] : null;
        
        // Validate the transition from previous item
        let isTargetOfTransition = false;
        if (previousItem && previousItem.transitionType && previousItem.transitionDuration) {
          const gap = item.start - previousItem.end;
          const transitionDuration = previousItem.transitionDuration;
          // Only show blue left border if transition is VALID (gap <= 0 or gap < duration)
          isTargetOfTransition = gap <= 0 || gap < transitionDuration;
        }
        
        return (
          <div
            key={`media-${item.id}-${index}`}
            className={`${styles.mediaMarker} ${draggedItem?.id === item.id ? styles.dragging : ''} ${selectedMediaItemId === item.id ? styles.selected : ''} ${trackClass} ${isHidden ? styles.hidden : ''}`}
            style={{
              left: `${timeToPercent(item.start)}%`,
              width: `${timeToPercent(item.end - item.start)}%`,
              backgroundColor: segmentColors[index % segmentColors.length],
              // Blue right border if this item has a transition
              borderRight: hasActiveTransition ? '3px solid #3b82f6' : undefined,
              // Blue left border if previous item has a VALID transition to this item
              borderLeft: isTargetOfTransition ? '3px solid #3b82f6' : undefined,
              opacity: isNotLoaded ? 0.3 : (draggedItem?.id === item.id ? 0.5 : undefined), // Dark when not loaded
              filter: isNotLoaded ? 'grayscale(70%)' : undefined, // Gray when not loaded
            }}
            onClick={(e) => {
              if (!draggedItem && !resizingState) {
                onMediaMarkerClick(e, item);
              }
            }}
            onMouseDown={(e) => handleDragStart(e, item)}
          >
            {/* Not loaded indicator */}
            {isNotLoaded && (
              <div 
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  fontSize: '12px',
                  pointerEvents: 'none',
                  opacity: 0.8
                }}
                title="Click to load segment"
              >
                ⏳
              </div>
            )}
            
            {/* Hidden indicator badge */}
            {isHidden && (
              <div 
                className={styles.hiddenIndicator}
                title="Hidden - will be excluded from video generation"
              >
                🙈
              </div>
            )}
            
            {/* Marker content */}
            <div className={styles.markerContent}>
              {item.type === "video" ? (
                <div
                  className={styles.markerWrapper}
                  style={{
                    border: draggedItem?.id === item.id && isAligned ? '2px solid green' : 'none',
                    boxSizing: 'border-box',
                    width: '100%',
                    height: '100%',
                  }}
                >
                  <video src={item.url} className={styles.mediaVideo} muted />
                </div>
              ) : (
                item.src ? (
                  <img src={item.src} alt={item.name} className={styles.mediaImage} />
                ) : (
                  <div className={styles.mediaText}>{item.name}</div>
                )
              )}
            </div>
            {/* Left resize handle */}
            <div
              className={styles.resizeHandleLeft}
              onMouseDown={(e) => { e.stopPropagation(); handleResizeStart(e, item, 'left'); }}
              title="Drag to adjust start time"
              style={{
                backgroundColor: unconnected && unconnected.left ? 'red' : 'rgba(0, 0, 0, 0.7)',
              }}
            />
            {/* Right resize handle */}
            <div
              className={styles.resizeHandleRight}
              onMouseDown={(e) => { e.stopPropagation(); handleResizeStart(e, item, 'right'); }}
              title="Drag to adjust end time"
              style={{
                backgroundColor: unconnected && unconnected.right ? 'red' : 'rgba(0, 0, 0, 0.7)',
              }}
            />
          </div>
        );
      })}
      
      {/* Overlap Region Visualizations */}
      {mediaItems.filter(item => !item.hidden).map((item, index, visibleItems) => {
        // Find next item on timeline
        const sortedItems = [...visibleItems].sort((a, b) => a.start - b.start);
        const currentIndex = sortedItems.findIndex(i => i.id === item.id);
        const nextItem = currentIndex < sortedItems.length - 1 ? sortedItems[currentIndex + 1] : null;
        
        if (!nextItem) return null; // No next item
        
        const gap = nextItem.start - item.end;
        if (gap >= 0) return null; // No overlap (gap or touching)
        
        // CRITICAL: Only show overlap region if current item has a VALID transition
        const hasValidTransition = 
          item.transitionType != null && 
          item.transitionType !== '' && 
          item.transitionType !== 'none' &&
          item.transitionDuration != null && 
          item.transitionDuration > 0;
        
        if (!hasValidTransition) return null; // No transition set, don't show overlap
        
        // Calculate overlap region
        const overlapStart = nextItem.start;
        const overlapEnd = item.end;
        const overlapDuration = overlapEnd - overlapStart;
        
        // Determine track class
        const trackClass = item.type === 'video' ? styles.videoTrack : styles.imageTrack;
        
        return (
          <div
            key={`overlap-${item.id}-${nextItem.id}`}
            className={`${styles.overlapRegion} ${trackClass}`}
            style={{
              left: `${timeToPercent(overlapStart)}%`,
              width: `${timeToPercent(overlapDuration)}%`,
            }}
            title={`Transition: ${item.transitionType} (${item.transitionDuration}s) | Overlap: ${overlapDuration.toFixed(2)}s`}
          />
        );
      })}
    </div>
  );
};

export default MediaMarkers;
